import { Controller } from "@hotwired/stimulus";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // bytes, matches the hint under the zone

// Resume drop zone: drag and drop onto the label, type and size checks, selected file names
export default class extends Controller<HTMLElement> {
  static targets = ["input", "files", "hint"];

  declare readonly inputTarget: HTMLInputElement;
  declare readonly filesTarget: HTMLElement;
  declare readonly hintTarget: HTMLElement;

  // dragenter/dragleave fire for every child the pointer crosses; count them to know when it really leaves
  private dragDepth = 0;
  private defaultHint = "";

  connect() {
    this.defaultHint = this.hintTarget.textContent ?? "";
  }

  dragenter(event: DragEvent) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth += 1;
    this.element.classList.add("is-dragover");
  }

  dragover(event: DragEvent) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  }

  dragleave() {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.element.classList.remove("is-dragover");
  }

  drop(event: DragEvent) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    this.dragDepth = 0;
    this.element.classList.remove("is-dragover");
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (this.accept(files)) {
      // Dropped files reach the form only through the input; a DataTransfer is the way to fill it
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      this.inputTarget.files = transfer.files;
    }
  }

  change() {
    this.accept(Array.from(this.inputTarget.files ?? []));
  }

  private accept(files: File[]): boolean {
    const error = this.validate(files);
    if (error) this.inputTarget.value = "";
    this.render(error ? [] : files, error);
    return error === null;
  }

  private validate(files: File[]): string | null {
    if (files.length === 0) return null;
    if (!this.inputTarget.multiple && files.length > 1) return "Please choose one file.";

    const allowed = this.inputTarget.accept.split(",").map((ext) => ext.trim().toLowerCase()).filter(Boolean);
    const wrongType = files.find((file) => allowed.length && !allowed.some((ext) => file.name.toLowerCase().endsWith(ext)));
    if (wrongType) return `${wrongType.name} is not a supported format.`;

    const tooBig = files.find((file) => file.size > MAX_FILE_SIZE);
    if (tooBig) return `${tooBig.name} is larger than 10MB.`;

    return null;
  }

  private render(files: File[], error: string | null = null) {
    this.filesTarget.replaceChildren(
      ...files.map((file) => {
        const item = document.createElement("span");
        item.className = "upload__file";
        item.textContent = `${file.name} · ${this.formatSize(file.size)}`;
        return item;
      })
    );

    this.element.classList.toggle("has-files", files.length > 0);
    this.element.classList.toggle("is-invalid", error !== null);
    // Pending client review: the replace and error wording below is not in the frames
    this.hintTarget.textContent = error ?? (files.length ? "Click or drop to replace" : this.defaultHint);
  }

  private formatSize(bytes: number) {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(/\.0$/, "")} MB`;
  }

  // Dragging selected text or a link over the zone should not light it up
  private hasFiles(event: DragEvent) {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  }
}
